import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { OrderType, TransactionType, OrderDuration, AddlOrdData } from '../models/common/trading-common.model';

export function SymbolValidator(control: AbstractControl) {

    if (control.pristine) {
        return null;
    }

    let value = control.value as string;

    let transactionType = control.parent.get('transactionType').value as TransactionType;
    if (! value) {
        return {SYMBOL_IS_REQUIRED: true};
    }

    if (! transactionType) {
       return {TRANSACTIONTYPE_IS_REQUIRED: true};
    }

    return null;
}

export function QtyValidator(control: AbstractControl) {

  if (control.pristine) {
      return null;
  }

  let value = control.value as number;
  let duration = control.parent.get('duration').value as OrderDuration;

  // Doing an explicit null check here rather than falsy check, as value of 0
  // evaluates to true with a falsy test and we want to explicitly show a
  // different message for value 0 or negative
  if (value == null ) {
      return {QTY_IS_REQUIRED: true};
  }

  if ( value < 1 ) {
      return {QTY_CANNOT_BE_LESS_THAN_1: true};
  }

  if ( value >= 1000000000 ) {
      return {QTY_CANNOT_BE_MORE_THAN_999999999: true};
  }

  // Make sure we have no more than places decimal places
  if ( value !== Math.round(value) ) {
      return {QTY_CANNOT_BE_DECIMAL: true};
  }

  if ( (duration === OrderDuration.IOC || duration === OrderDuration.FOK) && value < 100 ) {
      return {IOC_FOK_CANNOT_BE_LESS_THAN_100: true};
  }

  if ( (/^0\d*$/).test(String(value)) ) {  // can only be up to 9 digits whole number and no leading zeros
      return {QTY_NUMBER_FORMAT_ERROR: true};
  }

  return null;
}

export function FractionalQtyValidator(control: AbstractControl) {

  if (control.pristine) {
      return null;
  }

  let value = control.value as number;
  if (Number.isInteger(value) && value > 0) {  // if the input value is whole number just do validation as existing whole number shares
      return QtyValidator(control);
  }

  let duration = control.parent.get('duration').value as OrderDuration;

  if (value === null ) {
      return {QTY_IS_REQUIRED: true};
  }

  if (value <= 0 ) {
      return {QTY_SHOULD_BE_GREATER_THAN_0: true};
  }

  if ( (value >= 1 && !(/^[1-9]\d{0,6}(\.\d{1,4})$/).test(String(value))) || (value < 1 && !(/^0(\.\d{1,4})$/).test(String(value))) ) {
    // value must be either:
    // - up to 7 digits whole number and up to 4 decimal digits
    // - in format 0.####
    return checkFractionalQtyFormatErrors(value);
  }


  if ( (duration === OrderDuration.IOC || duration === OrderDuration.FOK) && value < 100 ) {
      return {IOC_FOK_CANNOT_BE_LESS_THAN_100: true};
  }
  return null;
}

function checkFractionalQtyFormatErrors(value) {
    let intLength = value.toString().split(".")[0].length;
    let decimalLength = value.toString().split(".")[1].length;
    if (decimalLength > 4) {
        return {FRACTIONAL_SHARES_DECIMAL_DIGITS: true};
    }
    if (intLength > 7) {
        return {FRACTIONAL_SHARES_INT_DIGITS: true};
    }
    return {FRACTIONAL_SHARES_NUMBER_FORMAT: true};
}

export function createDollarQtyValidator(minVal: number, maxVal: number): ValidatorFn {
  return (control:AbstractControl) => {
    const MIN_DOLLAR_VALUE = minVal;
    const MAX_DOLLAR_VALUE = maxVal;

    if ( control.pristine) {
        return null;
    }

    let value = control.value as number;

    if ( !Number.isInteger(value) ) {
        return {DOLLAR_TRADING_NOT_INTEGER: true};
    }

    if ( value < MIN_DOLLAR_VALUE ) {
        return {DOLLAR_TRADING_AMT_LESS_THAN: true};
    }

    if ( value > MAX_DOLLAR_VALUE ) {
        return {DOLLAR_TRADING_AMT_GREATER_THAN: true};
    }

  }
}

export function ShareQtyValidator(control: AbstractControl) {
  const MIN_SHARE_VALUE: number = 0.001;
  const MAX_SHARE_VALUE: number = 9999999.999;
  const SHARE_QTY_FORMAT: RegExp = /^\d{1,7}(\.\d{1,3})?$/;

  if ( control.pristine) {
      return null;
  }

  let value = control.value as number;

  if ( value < MIN_SHARE_VALUE || value > MAX_SHARE_VALUE ) {
      return {SHARE_QTY_RANGE_INVALID: true};
  }

  if ( !value.toString().match(SHARE_QTY_FORMAT) ) {
      return {SHARE_QTY_FORMAT_INVALID: true};
  }
}


export function LimitPriceValidator(control: AbstractControl) {

    if (control.pristine) {
        return null;
    }

    let orderType = control.parent.get('orderType').value as OrderType;
    let symbol = control.parent.get('symId').value;

    if (!symbol) {
        return {'ENTER_SYMBOL_FIRST': true};
    }

    if (!control.value) {
        return {'required': true};
    }

    if ( orderType === OrderType.LIMIT || orderType === OrderType.LIMIT_ON_CLOSE || orderType === OrderType.STOP_LIMIT) {
        let value: number = control.value;
        // Allow price upto eight whole number places and three decimal places, or seven whole number places with four decimals
        let validationLengthSevenRegExp: RegExp = /(^((?=.*[1-9])\d{1,7})?(?:\.\d{0,4})?$)/;
        let validationLengthEightRegExp: RegExp = /(^(?=.*[1-9])\d{1,8}(?:\.\d{0,3})?$)/;
        if (value != null && (!value.toString().match(validationLengthSevenRegExp) && !value.toString().match(validationLengthEightRegExp))) {
            // Multiple conditions to check for so we show the right error message
            // 1. Integer portion is too long (8 digits max)
            // 2. Max 3 decimals with 8 integer digits
            // 3. Max 4 decimals with 7 or fewer integer digits
            return checkForMultipleErrorCondition(value);
        }
    }

    return null;
}

function checkForMultipleErrorCondition(value: number): any{
    let decimalIndex: number = value.toString().indexOf('.');
    let integerPart: string = decimalIndex !== -1 ? value.toString().substring(0, decimalIndex) : value.toString();
    let decimalPart: string = decimalIndex !== -1 ? value.toString().substring(decimalIndex + 1) : '';

    if ( integerPart.indexOf('-') === 0 ) {
        return {PRICE_CANNOT_BE_NEGATIVE: true};
    }

    if ( integerPart.length > 8 ) {
        return {PRICE_MUST_HAVE_NO_MORE_THAN_EIGHT_WHOLE_NUMBER_DIGITS: true};
    }

    if ( integerPart.length === 8 && decimalPart.length > 3 ) {
        return {PRICE_MUST_HAVE_NO_MORE_THAN_THREE_DECIMALS: true};
    }

    // only possibility left since we failed the regular expression check is that integer
    // portion is less than 8 characters, which means decimal part is more than 4
    return {PRICE_MUST_HAVE_NO_MORE_THAN_FOUR_DECIMALS: true};
}

export function StopPriceValidator(control: AbstractControl) {

    if (control.pristine) {
        return null;
    }

    let orderType = control.parent.get('orderType').value as OrderType;
    let symbol = control.parent.get('symId').value;

    if (!symbol) {
        return {'ENTER_SYMBOL_FIRST': true};
    }

    if (!control.value) {
      return {'required': true};
    }

    if ( orderType === OrderType.STOP || orderType === OrderType.STOP_LIMIT) {
        let value: number = control.value;
        // Allow price upto eight whole number places and three decimal places, or seven whole number places with four decimals
        let validationLengthSevenRegExp: RegExp = /(^((?=.*[1-9])\d{1,7})?(?:\.\d{0,4})?$)/;
        let validationLengthEightRegExp: RegExp = /(^(?=.*[1-9])\d{1,8}(?:\.\d{0,3})?$)/;
        if ( value != null && (!value.toString().match(validationLengthSevenRegExp) && !value.toString().match(validationLengthEightRegExp) )) {
            // Multiple conditions to check for so we show the right error message
            // 1. Value is negative
            // 2. Integer portion is too long (8 digits max)
            // 3. Max 3 decimals with 8 integer digits
            // 4. Max 4 decimals with 7 or fewer integer digits
            return checkForMultipleErrorCondition(value);
        }
    }

    return null;
}

export function TrailDollarQtyValidator(control: AbstractControl) {

    if ( control.pristine ) {
        return null;
    }

    if (!control.value) {
      return {'required': true};
    }

    let orderType = control.parent.get('orderType').value as OrderType;

    if ( orderType === OrderType.TRAILING_STOP_DOLLAR || orderType === OrderType.TRAILING_STOP_LIMIT_DOLLAR || orderType === OrderType.TRAILING_STOP_LIMIT_AON) {
        let value: number = control.value;
        // Allow only amount ranges between 0.01  to 30.00 with only two decimal places
        let validationRegExp: RegExp = /^\d*(?:\.\d{0,2})?$/;

        let trailDollarQtyNumVal: number = Number.parseFloat(value.toString());
        if ( trailDollarQtyNumVal < 0.01 || trailDollarQtyNumVal > 30.00 || (value != null && !value.toString().match(validationRegExp)) ) {
            return {TRAIL_DOLLARQTY_INVALID: true};
        }
    }

    return null;
}

export function TrailPctQtyValidator(control: AbstractControl) {

    if ( control.pristine ) {
        return null;
    }

    if (!control.value) {
      return {'required': true};
    }

    let value = control.value;

    let orderType = control.parent.get('orderType').value as OrderType;

    if ( orderType === OrderType.TRAILING_STOP_PCT || orderType === OrderType.TRAILING_STOP_LIMIT_PCT || orderType === OrderType.TRAILING_STOP_LIMIT_AON || orderType === OrderType.TRAILING_STOP_LIMIT_PCT_AON) {

        // Allow only amount ranges between 1  to 30 with only whole numbers
        if ( value < 1 || value > 30 || value !== Math.round(value) ) {
            return {TRAIL_PCTQTY_INVALID: true};
        }
    }

    return null;
}

export function LimitOffsetValidator(control: AbstractControl) {

    if ( control.pristine ) {
        return null;
    }

    if (!control.value) {
      return {'required': true};
    }

    let orderType = control.parent.get('orderType').value as OrderType;

    if ( orderType === OrderType.TRAILING_STOP_LIMIT_DOLLAR || orderType === OrderType.TRAILING_STOP_LIMIT_PCT || orderType === OrderType.TRAILING_STOP_LIMIT_AON || orderType === OrderType.TRAILING_STOP_LIMIT_PCT_AON) {
        let value: number = control.value;
        // Allow only amount ranges between 0.00  to 1.00 with only two decimal places
        let validationRegExp: RegExp = /^\d*(?:\.\d{0,2})?$/;
        let trailLimitOffsetNumVal: number = Number.parseFloat(value.toString());
        if ( trailLimitOffsetNumVal < 0.00 || trailLimitOffsetNumVal > 1.00 || (value != null && !value.toString().match(validationRegExp)) ) {
            return {TRAIL_LIMIT_OFFSET_INVALID: true};
        }
    }

    return null;
}

    function isEmpty(param: any): boolean {
        return (param === undefined || param === null || param === '');
    }

export function SplitIPCodeValidator(control: AbstractControl) {
    if ( control.pristine ) {
        return null;
    }

    let ipSelectorVal = control.parent.get('ip').value as string;

    // now verify the IP codes are different, as long as both code fields are populated and valid
    let splitIP1 = control.parent.get('splitIP1') as FormControl;
    let splitIP2 = control.parent.get('splitIP2') as FormControl;
    let splitIP1Pct = control.parent.get('splitIP1Pct') as FormControl;
    let splitIP2Pct = control.parent.get('splitIP2Pct') as FormControl;

    let ipSelectValsAreNull = isEmpty(splitIP1) && isEmpty(splitIP2) && isEmpty(splitIP1Pct) && isEmpty(splitIP2Pct);
        

    if ( ipSelectorVal === 'split'  || !ipSelectValsAreNull) {


        let required = checkSplitIPIsRequired(splitIP1Pct,splitIP2Pct,splitIP1,splitIP2,control);
        if(required !== null) {
            return required;
        }  
        return checkSplitIPDifferentialErrors(splitIP1,splitIP2,control);
    }

    return null;
}

function checkSplitIPIsRequired(splitIP1Pct,splitIP2Pct,splitIP1,splitIP2,control) {
    
    // if any IPPct has value -
    if ( ((control === splitIP1) && hasText(splitIP1Pct.value) ) || ((control === splitIP2) && hasText(splitIP2Pct.value)) ) {
        // set required for this empty splitIP field
        if (!hasText(control.value)) {
          return {'required': true};
        }
    }

    // if any splitIPPct not 100, need mark all fileds - be careful have to use - splitIP1Pct.value !== null ( as !0 ==> true )
    if ( (hasText(splitIP1Pct.value) && splitIP1Pct.value !== 100) || (hasText(splitIP2Pct.value) && splitIP2Pct.value !== 100) ) {
        if (!hasText(control.value) && (hasText(splitIP1.value) || hasText(splitIP2.value) || hasText(splitIP1Pct.value) || hasText(splitIP2Pct.value))) {
            return {'required': true};
        }
    }
    return null;
}

function checkSplitIPDifferentialErrors(splitIP1,splitIP2,control) {
    if (
        hasText(splitIP1.value) && isOtherwiseValidIPPct(splitIP1) &&
        hasText(splitIP2.value) && isOtherwiseValidIPPct(splitIP2)
    ) {
        if ( splitIP1.value === splitIP2.value ) {
            return {SPLIT_IP_CODES_MUST_BE_DIFFERENT: true};
        } else {
            // make sure to clear any error for identical codes that might have been set on the other control,
            // such as if the user first set the value on the other control, triggered the identical values error,
            // then updated the currently active control to make the values different. We have to manually delete
            // the error key since it's not a standard one in the validator API
            if ( control === splitIP1 && !!splitIP2.errors && !!splitIP2.errors.SPLIT_IP_CODES_MUST_BE_DIFFERENT ) {
                delete splitIP2.errors.SPLIT_IP_CODES_MUST_BE_DIFFERENT;
                splitIP2.updateValueAndValidity({emitEvent: false});
            } else if ( control === splitIP2 && !!splitIP1.errors && !!splitIP1.errors.SPLIT_IP_CODES_MUST_BE_DIFFERENT ) {
                delete splitIP1.errors.SPLIT_IP_CODES_MUST_BE_DIFFERENT;
                splitIP1.updateValueAndValidity({emitEvent: false});
            }
        }
    }
    return null;
}

export function SplitIPPercentValidator(control: AbstractControl) {
    if ( control.pristine ) {
        return null;
    }

    let ipSelectorVal = control.parent.get('ip').value as string;

        // now check the total of the two Split IP percentage fields, but only as long
        // as both percentages are defined and valid
        let splitIP1Pct = control.parent.get('splitIP1Pct') as FormControl;
        let splitIP2Pct = control.parent.get('splitIP2Pct') as FormControl;
        let splitIP1 = control.parent.get('splitIP1') as FormControl;
        let splitIP2 = control.parent.get('splitIP2') as FormControl;

        let ipSelectValsAreNull = isEmpty(splitIP1) && isEmpty(splitIP2) && isEmpty(splitIP1Pct) && isEmpty(splitIP2Pct);

    if ( ipSelectorVal === 'split' ||  !ipSelectValsAreNull) {

        

        let required = checkSplitIPPctIsRequired(splitIP1Pct,splitIP2Pct,splitIP1,splitIP2,control);

        if(required !== null) {
            return required;
        }

        return checkSplitIPPctTotalErrors(splitIP1Pct,splitIP2Pct,control);
    }

    return null;
}

function checkSplitIPPctIsRequired(splitIP1Pct,splitIP2Pct,splitIP1,splitIP2,control) {
    // if any IP has value
    if ( ((control === splitIP1Pct) && hasText(splitIP1.value)) || ((control === splitIP2Pct) && hasText(splitIP2.value)) ) {
        // set required for this empty splitIPPct field
        if (!hasText(control.value)) {
          return {'required': true};
        }
    }

    // if any splitIPPct not 100, need mark all fileds - be careful have to use - splitIP1Pct.value !== null ( as !0 ==> true )
    if ( (hasText(splitIP1Pct.value) && splitIP1Pct.value !== 100) || (hasText(splitIP2Pct.value) && splitIP2Pct.value !== 100) ) {
      if (!hasText(control.value) && (hasText(splitIP1.value) || hasText(splitIP2.value) || hasText(splitIP1Pct.value) || hasText(splitIP2Pct.value))) {
          return {'required': true};
      }
    }
    return null;
}

function checkSplitIPPctTotalErrors(splitIP1Pct,splitIP2Pct,control) {
    if (
        hasText(splitIP1Pct.value) && isOtherwiseValidIPPct(splitIP1Pct) &&
        hasText(splitIP2Pct.value) && isOtherwiseValidIPPct(splitIP2Pct)
    ) {
        let splitIP1PctNumVal: number = Number.parseInt(splitIP1Pct.value);
        let splitIP2PctNumVal: number = Number.parseInt(splitIP2Pct.value);

        if ( splitIP1PctNumVal + splitIP2PctNumVal !== 100 ) {
            return {PERCENTS_MUST_TOTAL_100_FOR_SPLIT: true};
        } else {
            // make sure to clear any error for percent total that might have been set on the other control,
            // such as if the user first set the value on the other control, triggered the total error,
            // then updated the currently active control to fix the total. We have to manually delete the
            // error key since it's not a standard one in the validator API
            if ( control === splitIP1Pct && !!splitIP2Pct.errors && !!splitIP2Pct.errors.PERCENTS_MUST_TOTAL_100_FOR_SPLIT ) {
                delete splitIP2Pct.errors.PERCENTS_MUST_TOTAL_100_FOR_SPLIT;
                splitIP2Pct.updateValueAndValidity({emitEvent: false});
            } else if ( control === splitIP2Pct && !!splitIP1Pct.errors && !!splitIP1Pct.errors.PERCENTS_MUST_TOTAL_100_FOR_SPLIT ) {
                delete splitIP1Pct.errors.PERCENTS_MUST_TOTAL_100_FOR_SPLIT;
                splitIP1Pct.updateValueAndValidity({emitEvent: false});
            }
        }
    }
    return null;
}

export function NonDollarLargeQtyOrderQualifierValidator(control: FormControl): ValidationErrors | null {
  let valuesList = control.value;

  if (valuesList && Array.isArray(valuesList) && valuesList.indexOf(AddlOrdData.NOTHELD) >= 0) {
    return null;
  } else {
    return { NON_DOLLAR_LARGE_QTY_ORD_NOT_NOTHELD: true };
  }
}

export function SplitIPMultiFieldValidator(group: FormGroup) {
    if ( !group.touched ) {
        return null;
    }

    let ipSelectorVal = group.get('ip').value as string;

    let splitIP1: AbstractControl = group.get('splitIP1');
    let splitIP2: AbstractControl = group.get('splitIP2');
    let splitIP1Pct: AbstractControl = group.get('splitIP1Pct');
    let splitIP2Pct: AbstractControl = group.get('splitIP2Pct');

    let ipSelectValsAreNull = isEmpty(splitIP1) && isEmpty(splitIP2) && isEmpty(splitIP1Pct) && isEmpty(splitIP2Pct);

    if ( ipSelectorVal === 'split' || !ipSelectValsAreNull) {

        if ( splitIP1 === null && splitIP1Pct === null && splitIP2 === null && splitIP2Pct === null ) {
            return;
        }

        let splitIP1Val = getValueAsString(splitIP1);
        let splitIP1PctVal = getValueAsString(splitIP1Pct);
        let splitIP2Val = getValueAsString(splitIP2);
        let splitIP2PctVal = getValueAsString(splitIP2Pct);

        // identify which field has error
        splitIP1.markAsDirty({ onlySelf: true });
        splitIP1.updateValueAndValidity({ onlySelf: true });
        splitIP1Pct.markAsDirty({ onlySelf: true });
        splitIP1Pct.updateValueAndValidity({ onlySelf: true });
        splitIP2.markAsDirty({ onlySelf: true });
        splitIP2.updateValueAndValidity({ onlySelf: true });
        splitIP2Pct.markAsDirty({ onlySelf: true });
        splitIP2Pct.updateValueAndValidity({ onlySelf: true });

        // All must be null/blank, OR all must have text that is valid, OR IP 1 must be 100% with no values for IP2
        if (
            ( !hasText(splitIP1Val) && !hasText(splitIP1PctVal) && !hasText(splitIP2Val) && !hasText(splitIP2PctVal) ) ||
            ( hasText(splitIP1Val) && splitIP1.valid && hasText(splitIP1PctVal) && splitIP1Pct.valid &&
              hasText(splitIP2Val) && splitIP2.valid && hasText(splitIP2PctVal) && splitIP2Pct.valid ) ||
            ( hasText(splitIP1Val) && splitIP1.valid && hasText(splitIP1PctVal) && splitIP1Pct.valid && splitIP1Pct.value === 100 &&
              !hasText(splitIP2Val) && !hasText(splitIP2PctVal) )
        ) {
            return null;
        }

        // We did validation at each field, so this is no use
        // return {ALL_SPLIT_FIELDS_MUST_BE_SET: true};
    }

    return null;
}

function getValueAsString(control) {
    if(control===null) {
        return null; 
    } else {
        return control.value as string;
    }
}

function hasText(testVal: string): boolean {
    return testVal !== null && testVal !== '';
}

function isOtherwiseValidIPPct(control: FormControl): boolean {
    if ( !control.errors ) {
        return true;
    }

    return !control.errors.min && !control.errors.max && !control.errors.pattern;
}
